import {
  fetchAggregationAspectsList,
  fetchAggregationResultsRows,
  fetchAggregationResultsSentimentRows,
  fetchExternalResourceCategories,
  type AggregationAspectListItem,
  type AggregationAspectRow,
  type AggregationResultsCategoryRow,
  type AggregationResultsSentimentRow,
  type ExternalResourceAspect,
  type ExternalResourceCategory,
} from '../../../../../store/api/monitoring-api';
import { formatExternalMonitoringPeriodLabelRu } from '../../../../../helpers/external-monitoring-period';
import {
  aggregateExternalMonitoringDashboard,
  type ExternalMonitoringChartsPayload,
  type ExternalMonitoringKpiPayload,
} from './external-monitoring-aggregate';
import type { PyramidChartApi } from './charts/use-aspects-pyramid';

function listToNameRecord(list: { id: number; name: string }[]): Record<number, string> {
  const r: Record<number, string> = {};
  list.forEach((item) => {
    r[item.id] = item.name;
  });
  return r;
}

/** Сырые строки results + `category` и на каждом аспекте объект `aspect` из справочников GET /categories и /aspects */
export type AggregationAspectRowEnriched = AggregationAspectRow & {
  aspect: { id: number; name: string };
};

export type AggregationResultsCategoryRowEnriched = Omit<AggregationResultsCategoryRow, 'aspects'> & {
  category: { id: number; name: string };
  aspects?: AggregationAspectRowEnriched[];
};

function enrichRowsWithAspectsAndCategories(
  rows: AggregationResultsCategoryRow[],
  aspectsList: AggregationAspectListItem[],
  categoriesList: ExternalResourceCategory[],
): AggregationResultsCategoryRowEnriched[] {
  const aspectById = new Map(aspectsList.map((a) => [a.id, a]));
  const categoryById = new Map(categoriesList.map((c) => [c.id, c]));

  return rows.map((row) => {
    const cid = Number(row.category_id);
    const cat = Number.isFinite(cid) ? categoryById.get(cid) : undefined;
    const category: { id: number; name: string } = cat
      ? { id: cat.id, name: cat.name }
      : { id: cid, name: `Категория ${cid}` };

    return {
      ...row,
      category,
      aspects: (row.aspects || []).map((a) => {
        const aid = Number(a.aspect_id);
        const asp = Number.isFinite(aid) ? aspectById.get(aid) : undefined;
        const aspect: { id: number; name: string } = asp
          ? { id: asp.id, name: asp.name }
          : { id: aid, name: `Аспект ${aid}` };
        return {
          ...a,
          aspect,
        };
      }),
    };
  });
}

/** KPI: всего комментариев, тональность, индекс — из `type=sentiment`; драйвер/риск и aspectsCount — из categories. */
export function mergeKpiWithSentimentTotals(
  kpi: ExternalMonitoringKpiPayload,
  sentimentRows: AggregationResultsSentimentRow[],
): ExternalMonitoringKpiPayload {
  let totalComments = 0;
  let positive = 0;
  let neutral = 0;
  let negative = 0;
  for (const row of sentimentRows) {
    totalComments += Number(row.comments_count ?? 0);
    positive += Number(row.positive_count ?? 0);
    neutral += Number(row.neutral_count ?? 0);
    negative += Number(row.negative_count ?? 0);
  }
  const rawSentimentIndex =
    totalComments > 0 ? ((positive - negative) / totalComments) * 100 : 0;
  const sentimentIndex = Math.max(-100, Math.min(100, rawSentimentIndex));
  return {
    ...kpi,
    totalComments,
    positive,
    neutral,
    negative,
    sentimentIndex,
  };
}

function formatPeriodFromRows(rows: AggregationResultsCategoryRow[]): string | undefined {
  const begins = rows.map((r) => r.begin).filter((x): x is number => x != null && Number.isFinite(x));
  const ends = rows.map((r) => r.end).filter((x): x is number => x != null && Number.isFinite(x));
  if (!begins.length || !ends.length) return undefined;
  const b = Math.min(...begins);
  const e = Math.max(...ends);
  const fmt = (ts: number) =>
    new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(ts * 1000));
  return `${fmt(b)} — ${fmt(e)}`;
}

/**
 * Единый объект данных дашборда: сырые строки results, справочники aspects/categories,
 * расширенные строки с `aspect` и `category`, KPI.
 */
export type ExternalMonitoringDashboardModel = {
  resourceId: number;
  period: {
    begin: number;
    end: number;
    labelRu: string;
    labelFromRows?: string;
  };
  rows: AggregationResultsCategoryRow[];
  rowsEnriched: AggregationResultsCategoryRowEnriched[];
  /** id аспекта → имя (из GET /aspects) */
  aspectNamesById: Record<number, string>;
  /** id категории → имя (из GET /categories) */
  categoryNamesById: Record<number, string>;
  /** Сырые списки справочников для графиков / отладки */
  aspectsList: AggregationAspectListItem[];
  categoriesList: ExternalResourceCategory[];
  kpiPayload: ExternalMonitoringKpiPayload;
  heatmapPayload: ExternalMonitoringChartsPayload['heatmap'];
  /** Бакеты GET aggregation/results?type=sentiment — для KPI (тональность) и «Динамика тональности» */
  sentimentRows: AggregationResultsSentimentRow[];
  sentimentDynamicsByDay: null;
};

/** Данные для графика «Аспекты по тональности» из enrichment results */
export function buildPyramidChartApiFromRowsEnriched(
  rowsEnriched: AggregationResultsCategoryRowEnriched[],
): PyramidChartApi {
  const categories: { id: string; name: string }[] = [];
  const seenCat = new Set<string>();
  for (const row of rowsEnriched) {
    const id = String(row.category_id);
    if (!seenCat.has(id)) {
      seenCat.add(id);
      categories.push({ id, name: row.category.name });
    }
  }

  const aspectsByKey = new Map<
    string,
    { categoryId: string; name: string; positive: number; negative: number }
  >();
  for (const row of rowsEnriched) {
    const categoryId = String(row.category_id);
    for (const a of row.aspects || []) {
      const aspectId = Number(a.aspect?.id);
      const aspectName = a.aspect.name;
      const key = Number.isFinite(aspectId)
        ? `${categoryId}:${aspectId}`
        : `${categoryId}:${aspectName}`;
      const existing = aspectsByKey.get(key) ?? {
        categoryId,
        name: aspectName,
        positive: 0,
        negative: 0,
      };
      existing.positive += Number(a.positive_count || 0);
      existing.negative += Number(a.negative_count || 0);
      aspectsByKey.set(key, existing);
    }
  }

  const aspects: PyramidChartApi['aspects'] = Array.from(aspectsByKey.values());

  return { categories, aspects };
}

/** Нет строк results или нет ни тональности по аспектам, ни comments_count по строкам — единая заглушка вместо сетки карточек */
export function isExternalMonitoringDashboardDataEmpty(model: ExternalMonitoringDashboardModel): boolean {
  const { rows, kpiPayload: kpi } = model;
  if (rows.length === 0) {
    const sentimentSum = kpi.positive + kpi.neutral + kpi.negative;
    if (sentimentSum > 0) return false;
    return kpi.totalComments <= 0;
  }
  const sentimentSum = kpi.positive + kpi.neutral + kpi.negative;
  if (sentimentSum > 0) return false;
  return kpi.totalComments <= 0;
}

/** Клиентский фильтр по `category_id` для KPI и графиков; период begin/end не меняется. */
export function applyCategoryFilterToDashboardModel(
  model: ExternalMonitoringDashboardModel,
  categoryId: 'all' | string | undefined,
): ExternalMonitoringDashboardModel {
  if (categoryId == null || categoryId === 'all') {
    return model;
  }
  const idNum = Number(categoryId);
  if (!Number.isFinite(idNum)) {
    return model;
  }

  const rows = model.rows.filter((r) => Number(r.category_id) === idNum);
  const rowsEnriched = model.rowsEnriched.filter((r) => Number(r.category_id) === idNum);
  const labelFromRows = formatPeriodFromRows(rows);
  const aspectsResolved: ExternalResourceAspect[] = model.aspectsList.map((a) => ({
    id: a.id,
    name: a.name,
  }));
  const periodLabelForCharts = labelFromRows ?? model.period.labelRu;

  const { kpi, charts } = aggregateExternalMonitoringDashboard(
    rows,
    model.categoriesList,
    aspectsResolved,
    periodLabelForCharts,
  );

  return {
    ...model,
    rows,
    rowsEnriched,
    kpiPayload: mergeKpiWithSentimentTotals(kpi, model.sentimentRows),
    heatmapPayload: charts.heatmap,
    period: {
      ...model.period,
      labelFromRows: labelFromRows ?? model.period.labelFromRows,
    },
  };
}

/*
 * Отключено: второй уровень KPI — срез дашборда по одному аспекту (после выбора категории).
 * Чтобы вернуть: раскомментировать, экспортировать снова и подключить в use-dashboard-external-monitoring.
 *
export function applyAspectFilterToDashboardModel(
  model: ExternalMonitoringDashboardModel,
  aspectId: 'all' | string | undefined,
): ExternalMonitoringDashboardModel {
  if (aspectId == null || aspectId === 'all') {
    return model;
  }
  const idNum = Number(aspectId);
  if (!Number.isFinite(idNum)) {
    return model;
  }

  const rows = model.rows
    .map((r) => ({
      ...r,
      aspects: (r.aspects ?? []).filter((a) => Number(a.aspect_id) === idNum),
    }))
    .filter((r) => (r.aspects?.length ?? 0) > 0);
  const rowsEnriched = model.rowsEnriched
    .map((r) => ({
      ...r,
      aspects: (r.aspects ?? []).filter((a) => Number(a.aspect_id) === idNum),
    }))
    .filter((r) => (r.aspects?.length ?? 0) > 0);
  const labelFromRows = formatPeriodFromRows(rows);
  const aspectsResolved: ExternalResourceAspect[] = model.aspectsList.map((a) => ({
    id: a.id,
    name: a.name,
  }));
  const periodLabelForCharts = labelFromRows ?? model.period.labelRu;

  const { kpi, charts } = aggregateExternalMonitoringDashboard(
    rows,
    model.categoriesList,
    aspectsResolved,
    periodLabelForCharts,
  );

  return {
    ...model,
    rows,
    rowsEnriched,
    kpiPayload: kpi,
    heatmapPayload: charts.heatmap,
    period: {
      ...model.period,
      labelFromRows: labelFromRows ?? model.period.labelFromRows,
    },
  };
}
 */

export async function loadExternalMonitoringDashboardModel(input: {
  /** Значение для query `resources`: один id или id через запятую */
  resources: string;
  /** Границы периода, unix секунды (как в aggregation/results). */
  periodBegin: number;
  periodEnd: number;
  groupingInterval?: 'day' | 'week' | 'month';
}): Promise<ExternalMonitoringDashboardModel> {
  const begin = input.periodBegin;
  const end = input.periodEnd;
  const labelRu = formatExternalMonitoringPeriodLabelRu(begin, end);
  const groupingInterval = input.groupingInterval ?? 'day';

  const [rows, aspectsList, categoriesList, sentimentRows] = await Promise.all([
    fetchAggregationResultsRows(input.resources, {
      begin,
      end,
      grouping_interval: groupingInterval,
      type: 'categories',
    }),
    fetchAggregationAspectsList(),
    fetchExternalResourceCategories(),
    fetchAggregationResultsSentimentRows(input.resources, {
      begin,
      end,
      grouping_interval: groupingInterval,
    }),
  ]);

  const aspectNamesById = listToNameRecord(aspectsList);
  const categoryNamesById = listToNameRecord(categoriesList);

  const rowsEnriched = enrichRowsWithAspectsAndCategories(rows, aspectsList, categoriesList);

  const aspectsResolved: ExternalResourceAspect[] = aspectsList.map((a) => ({
    id: a.id,
    name: a.name,
  }));

  const labelFromRows = formatPeriodFromRows(rows);
  const periodLabelForCharts = labelFromRows ?? labelRu;

  const { kpi, charts } = aggregateExternalMonitoringDashboard(
    rows,
    categoriesList,
    aspectsResolved,
    periodLabelForCharts,
  );

  const kpiPayload: ExternalMonitoringKpiPayload = mergeKpiWithSentimentTotals(kpi, sentimentRows);
  const heatmapPayload = charts.heatmap;

  const singleId = input.resources.includes(',') ? NaN : Number(input.resources);
  const resourceIdNum = Number.isFinite(singleId) ? singleId : 0;

  return {
    /** 0 — выбраны все источники (несколько id в `resources`) */
    resourceId: resourceIdNum,
    period: {
      begin,
      end,
      labelRu,
      labelFromRows,
    },
    rows,
    rowsEnriched,
    aspectNamesById,
    categoryNamesById,
    aspectsList,
    categoriesList,
    kpiPayload,
    heatmapPayload,
    sentimentRows,
    sentimentDynamicsByDay: null,
  };
}
